import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Snackbar,
  Switch,
  Typography,
} from '@mui/material';
import {
  getPromoErrorMessage,
  getPromoLivePreview,
  getPromoPayload,
  isPromoApiSuccess,
  listPromoSurfaces,
  setPromoSurfaceEnabled,
} from '../../services/promoSurfacesApi';
import LivePreviewPanel from './LivePreviewPanel';
import {
  creativeTypeLabel,
  getPromoSurface,
  parseLivePreview,
  parseSurfaceList,
  surfaceSwitchMessage,
  warningLabel,
} from './promoSurfacesUtils';
import { usePromoSurfacesSession } from './usePromoSurfacesSession';

function PromoSurfaceHub() {
  const [loading, setLoading] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [switchingKey, setSwitchingKey] = useState('');
  const [error, setError] = useState('');
  const [previewError, setPreviewError] = useState('');
  const [preview, setPreview] = useState(null);
  const [appVersion, setAppVersion] = useState('');
  const [rows, setRows] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { handleApiResponse } = usePromoSurfacesSession(setError);

  const load = useCallback(async () => {
    setError('');
    try {
      const result = await listPromoSurfaces();
      if (handleApiResponse(result, 'Could not load promo surfaces.')) {
        setRows([]);
        return;
      }
      const surfaces = parseSurfaceList(getPromoPayload(result)).surfaces;
      if (!surfaces.length) {
        setRows([]);
        setError('Could not load promo surfaces.');
        return;
      }
      setRows(surfaces);
    } catch (loadError) {
      setError(loadError.message || 'Could not load promo surfaces.');
    } finally {
      setLoading(false);
    }
  }, [handleApiResponse]);

  const loadPreview = useCallback(async () => {
    setPreviewLoading(true);
    setPreviewError('');
    try {
      const previewRes = await getPromoLivePreview(appVersion);
      if (isPromoApiSuccess(previewRes)) {
        setPreview(parseLivePreview(getPromoPayload(previewRes)));
      } else {
        setPreview(null);
        setPreviewError(getPromoErrorMessage(previewRes, 'Could not load the live preview.'));
      }
    } catch (previewLoadError) {
      setPreviewError(previewLoadError.message || 'Could not load the live preview.');
    } finally {
      setPreviewLoading(false);
    }
  }, [appVersion]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  const handleToggle = async (row, enabled) => {
    const meta = getPromoSurface(row.key);
    setSwitchingKey(row.key);
    setError('');
    try {
      const result = await setPromoSurfaceEnabled(row.key, enabled);
      if (handleApiResponse(result, enabled ? 'Could not turn this surface on.' : 'Could not turn this surface off.')) {
        return;
      }
      setSnackbar({
        open: true,
        message: surfaceSwitchMessage(getPromoPayload(result), meta?.title || row.key),
        severity: 'success',
      });
      await Promise.all([load(), loadPreview()]);
    } catch (toggleError) {
      setError(toggleError.message || 'Could not update this surface.');
    } finally {
      setSwitchingKey('');
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Each slot has its own On/Off switch and template library. At most one template per slot is active. Everything
        else is a draft. The app shows a slot only when the surface is on and that active template is eligible. Turning
        a surface off leaves the active template in place. All three slots can be on together.
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading ? (
        <CircularProgress size={28} />
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
          {rows.map((row) => {
            const meta = getPromoSurface(row.key);
            const active = row.active_template;
            const warning = warningLabel(row.warning);
            return (
              <Paper key={row.key} variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6">{meta?.title || row.key}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {meta?.summary} {meta?.creative}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(row.enabled)}
                        disabled={switchingKey === row.key}
                        onChange={(event) => handleToggle(row, event.target.checked)}
                        inputProps={{ 'aria-label': `${meta?.title || row.key} surface` }}
                      />
                    }
                    label={row.enabled ? 'On' : 'Off'}
                  />
                  <Chip
                    size="small"
                    color={row.serving ? 'success' : 'default'}
                    label={row.serving ? 'Showing in the app' : 'Not showing'}
                  />
                </Box>
                <Typography variant="body2" sx={{ my: 1 }}>
                  {active
                    ? `Active: ${active.name}${active.type ? ` · ${creativeTypeLabel(active.type)}` : ''}`
                    : 'No active template'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {Number(row.template_count) || 0} templates
                </Typography>
                {!active && row.last_active_template_id && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Turning this on restores the previous template when it is still eligible.
                  </Typography>
                )}
                {active?.thumbnail_url && (
                  <Box
                    component="img"
                    src={active.thumbnail_url}
                    alt=""
                    sx={{ width: 72, height: 96, objectFit: 'cover', borderRadius: 1, mt: 1 }}
                  />
                )}
                {warning && (
                  <Alert severity="warning" sx={{ mt: 1.5 }}>
                    {warning}
                  </Alert>
                )}
                <Button component={Link} to={`/admin/promo/${row.key}`} variant="contained" size="small" sx={{ mt: 1.5 }}>
                  Open library
                </Button>
              </Paper>
            );
          })}
        </Box>
      )}
      <Box sx={{ mt: 2 }}>
        <LivePreviewPanel
          preview={preview}
          loading={previewLoading}
          error={previewError}
          appVersion={appVersion}
          onApplyVersion={setAppVersion}
        />
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((current) => ({ ...current, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default PromoSurfaceHub;
